import { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { useApi } from '../../../../hooks/useApi';

export const ReassignModal = ({ isOpen, onClose, appointment, onReassign }) => {
  const { get, post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counselors, setCounselors] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    const fetchCounselors = async () => {
      try {
        const response = await get('/users/counselors');
        if (response.data?.status === 'success') {
          // Filter out current counselor and format counselor data
          const availableCounselors = response.data.data
            .filter(counselor => counselor._id !== appointment?.counselor?._id)
            .map(counselor => ({
              _id: counselor._id,
              // Use fullName if available, otherwise use username
              displayName: counselor.fullName || counselor.username,
              email: counselor.email
            }));
          
          setCounselors(availableCounselors);
        }
      } catch (error) {
        setError('Failed to load counselors. Please try again.');
      }
    };

    if (isOpen) {
      fetchCounselors();
      setSelectedCounselor('');
      setReason('');
      setError(null);
    }
  }, [isOpen, appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await post(`/appointments/${appointment._id}/reassign`, {
        counselorId: selectedCounselor,
        reason: reason
      });

      if (response.data?.status === 'success') {
        if (onReassign) await onReassign();
        onClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reassign appointment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !appointment) return null;

  // Get display names for current appointment details
  const clientDisplay = appointment.client?.fullName || appointment.client?.username || 'N/A';
  const currentCounselorDisplay = appointment.counselor?.fullName || appointment.counselor?.username || 'N/A';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Reassign Appointment</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Appointment Info */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500">Client</label>
                <p className="text-sm font-medium mt-1">{clientDisplay}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500">Current Counselor</label>
                <p className="text-sm font-medium mt-1">{currentCounselorDisplay}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500">Date</label>
                <p className="text-sm font-medium mt-1 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(appointment.date).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500">Time</label>
                <p className="text-sm font-medium mt-1 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {appointment.timeSlot}
                </p>
              </div>
            </div>
          </div>

          {/* Counselor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select New Counselor
            </label>
            <select
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
              value={selectedCounselor}
              onChange={(e) => setSelectedCounselor(e.target.value)}
              required
            >
              <option value="">Select a counselor</option>
              {counselors.map((counselor) => (
                <option key={counselor._id} value={counselor._id}>
                  {counselor.displayName} ({counselor.email})
                </option>
              ))}
            </select>
          </div>

          {/* Reason Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Reassignment
            </label>
            <textarea
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide a reason for reassigning this appointment..."
              required
            />
          </div>

          {/* Warning Message */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  This action will notify both counselors and the client about the reassignment.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedCounselor || !reason}
              className="px-4 py-2 text-sm font-medium text-white bg-uzima-maroon-500 border border-transparent rounded-md hover:bg-uzima-maroon-600 disabled:bg-blue-300"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Reassigning...
                </span>
              ) : (
                'Confirm Reassignment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};